import * as _ from 'underscore';

import { appStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { AlertErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IAlertInfo, IAlertListInfo } from '../models/IAlertInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { Alerts } from '../resources/Alerts';
import { ExtractTasks } from '../resources/ExtractTasks';

interface IAlertInfoScope extends ng.IScope {
  close: () => void;
  type: string;
  id: string;
  alert: IAlertInfo;

  getFailureType: (statusCode: number) => string;
  tryAgainClick: (taskId: string) => void;
  workbooksAffectedString: (workbookList: INamedItemInfo[]) => string;
  isRemoteRefresh: (statusCode: number) => boolean;
  workbookUrl: (workbookId: string) => string;
  datasourceUrl: (datasourceId: string) => string;
  datasourceConnectionsUrl: (datasourceId: string) => string;
}

Module.instance().directive('tbAlertInfo', <any[]>[
  '$translate',
  'Alerts',
  'ExtractTasks',
  (
    $translate: ng.translate.ITranslateService,
    Alerts: Alerts,
    ExtractTasks: ExtractTasks
  ) => {
    return {
      restrict: 'E',
      replace: true,
      templateUrl: 'alertInfo.html',
      scope: {
        close: '&',
        type: '=',
        id: '='
      },
      link: (scope: IAlertInfoScope) => {

        // Maps to the string version of the error code
        scope.getFailureType = (statusCode: number) => {
          switch (statusCode) {
            case AlertErrorCode.AlertSignInFailed:
            case AlertErrorCode.AlertRemoteSignInFailed:
            case AlertErrorCode.AlertNoPassword:
            case AlertErrorCode.AlertRemoteNoPassword:
              return $translate.instant('signInFailedUnknownReason');
            case AlertErrorCode.AlertExpiredPassword:
            case AlertErrorCode.AlertRemoteExpiredPassword:
              return $translate.instant('expiredPassword');
            case AlertErrorCode.AlertAccessTokenValidation:
              return $translate.instant('accessTokenValidation');
            case AlertErrorCode.AlertAuthConfiguration:
              return $translate.instant('authConfiguration');
            case AlertErrorCode.AlertConnectionFailure:
            case AlertErrorCode.AlertRemoteConnectionFailure:
              return $translate.instant('connectionFailure');
            case AlertErrorCode.AlertInvalidFilePath:
            case AlertErrorCode.AlertRemoteInvalidFilePath:
              return $translate.instant('invalidFilePath');
            case AlertErrorCode.AlertOverlappingSchedules:
              return $translate.instant('overlappingSchedules');
            case AlertErrorCode.AlertFullRefreshRequired:
              return $translate.instant('fullRefreshRequired');
            case AlertErrorCode.AlertRemoteTableNotFound:
              return $translate.instant('tableNotFound');
            case AlertErrorCode.AlertRemoteUnknownColumn:
              return $translate.instant('unknownColumn');
            case AlertErrorCode.AlertRemoteQueryCompilation:
              return $translate.instant('queryCompilationError');
            case AlertErrorCode.AlertRemoteFederatedError:
              return $translate.instant('federatedError');
            case AlertErrorCode.AlertRemoteInvalidSemiStructDataSchema:
              return $translate.instant('invalidSemiStructDataSchema');
            case AlertErrorCode.AlertRemoteUnableToConnectToFile:
              return $translate.instant('unableToConnectToFile');
            case AlertErrorCode.AlertConnectionlessFailure:
              return $translate.instant('connectionlessFailure');
            case AlertErrorCode.AlertConnectionNotWhitelisted:
              return $translate.instant('connectionNotWhitelisted');
            case AlertErrorCode.AlertRemoteNoDriver:
              return $translate.instant('noDriver');
            case AlertErrorCode.AlertRemoteIncompatibleDocumentVersion:
              return $translate.instant('incompatibleDocumentVersion');
            case AlertErrorCode.AlertRemoteWDCAbortError:
              return $translate.instant('wdcAbortError');
            case AlertErrorCode.AlertRemoteWDCInvalidData:
              return $translate.instant('wdcInvalidData');
            case AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime:
              return $translate.instant('extractRefreshNotFinishedOnTime');
            case AlertErrorCode.AlertRemoteBridgeClientNotRunning:
              return $translate.instant('bridgeClientNotRunning');
            default:
              // AlertErrorCode.AlertUnknownFailure or AlertErrorCode.AlertRemoteUnknownFailure
              return $translate.instant('unknownFailure');
          }
        };

        scope.tryAgainClick = (taskId: string) => {
          if (taskId) {
            ExtractTasks.run([taskId]);
          }
        };

        scope.isRemoteRefresh = (statusCode: number) => {
          const REMOTE_REFRESH_STATUS_CODES: number[] = [
            AlertErrorCode.AlertRemoteUnknownFailure,
            AlertErrorCode.AlertRemoteSignInFailed,
            AlertErrorCode.AlertRemoteExpiredPassword,
            AlertErrorCode.AlertRemoteConnectionFailure,
            AlertErrorCode.AlertRemoteNoPassword,
            AlertErrorCode.AlertRemoteInvalidFilePath,
            AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime,
            AlertErrorCode.AlertRemoteBridgeClientNotRunning,
          ];
          return _.contains(REMOTE_REFRESH_STATUS_CODES, statusCode);
        };

        // Simple formatting so the number of effected workbooks shown in the list of
        // alerts does not get too long
        scope.workbooksAffectedString = (workbookList: INamedItemInfo[]) => {
          if (workbookList) {
            const length = workbookList.length;
            if (length === 1) {
              return $translate.instant('outOfDateWithName', {
                NAME: L10n.elideIfLong(workbookList[0].name),
              });
            } else if (length > 1) {
              return $translate.instant('outOfDateWorkbooksWithCount', {
                LIST_COUNT: length
              }, L10n.Interpolations.Count);
            }
          }
          return $translate.instant('outOfDateWorkbookNone');
        };

        scope.workbookUrl = (workbookId: string) => {
          const siteUrls = siteUrlsSelector(appStore.getState());
          return siteUrls ? siteUrls.workbookDatasourcesUrl(workbookId) : '';
        };

        scope.datasourceUrl = (datasourceId: string) => {
          const siteUrls = siteUrlsSelector(appStore.getState());
          return siteUrls ? siteUrls.datasourceUrl(datasourceId) : '';
        };

        scope.datasourceConnectionsUrl = (datasourceId: string) => {
          const siteUrls = siteUrlsSelector(appStore.getState());
          return siteUrls ? siteUrls.datasourceConnectionsUrl(datasourceId) : '';
        };

        Alerts.getForType(scope.type, scope.id)
          .then((alertListInfo: IAlertListInfo) => {
            if (alertListInfo && alertListInfo.alerts && alertListInfo.alerts.length > 0) {
              scope.alert = alertListInfo.alerts[0];
            }
          });
      }
    };
  }
]);
